import { Helmet } from "react-helmet-async";
import { Cookie } from "lucide-react";
import { WebsiteHeader } from "@/components/layout/website-header";
import { WebsiteFooter } from "@/components/layout/website-footer";

const LAST_UPDATED = "October 14, 2026";

export default function CookiePolicyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Helmet>
        <title>Cookie Policy | SaaSGama</title>
        <meta name="description" content="Read SaaSGama's Cookie Policy to learn how and why we use cookies and similar technologies across our SaaS management platform." />
        <meta name="keywords" content="SaaSGama Cookie Policy, Cookies, Tracking Technologies" />
        <link rel="canonical" href="https://saasgama.com/cookie-policy" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://saasgama.com/cookie-policy" />
        <meta property="og:site_name" content="SaaSGama" />
        <meta property="og:title" content="Cookie Policy | SaaSGama" />
        <meta property="og:description" content="Read SaaSGama's Cookie Policy to learn how and why we use cookies and similar technologies across our SaaS management platform." />
        <meta property="og:image" content="https://saasgama.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Cookie Policy | SaaSGama" />
        <meta name="twitter:description" content="Read SaaSGama's Cookie Policy to learn how and why we use cookies and similar technologies across our SaaS management platform." />
        <meta name="twitter:image" content="https://saasgama.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "Organization", "@id": "https://saasgama.com/#organization", "name": "SaaSGama", "url": "https://saasgama.com", "logo": "https://saasgama.com/logo.png" },
            { "@type": "BreadcrumbList", "@id": "https://saasgama.com/cookie-policy#breadcrumb", "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://saasgama.com" },
              { "@type": "ListItem", "position": 2, "name": "Cookie Policy", "item": "https://saasgama.com/cookie-policy" }
            ]},
            { "@type": "WebPage", "@id": "https://saasgama.com/cookie-policy#webpage", "url": "https://saasgama.com/cookie-policy", "name": "Cookie Policy | SaaSGama", "description": "How and why SaaSGama uses cookies and similar technologies.", "breadcrumb": { "@id": "https://saasgama.com/cookie-policy#breadcrumb" } }
          ]
        })}</script>
      </Helmet>

      <WebsiteHeader showBackButton />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Cookie className="w-4 h-4" />
            How We Use Cookies
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Cookie Policy</h1>
          <p className="text-blue-100">Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      {/* Policy content */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12 space-y-10 text-gray-700 leading-relaxed">

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">1. What Are Cookies</h2>
              <p>
                Cookies are small text files that are placed on your computer or mobile device when you visit a
                website. They are widely used to make websites work, or work more efficiently, as well as to provide
                information to the owners of the site. Similar technologies include web beacons, pixels, local storage,
                and SDKs, which we refer to collectively as "cookies" in this policy. This Cookie Policy should be read
                alongside our{" "}
                <a href="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</a>, which explains
                more broadly how we handle your personal information.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">2. How We Use Cookies</h2>
              <p>
                We use cookies and similar tracking technologies to operate our platform, keep you securely signed in,
                remember your preferences, understand how our platform is used so we can improve it, and, where you
                consent, to support marketing and advertising. You can accept or manage non-essential cookies through
                the cookie banner shown when you first visit our site.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">3. Types of Cookies We Use</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-900">Strictly Necessary Cookies</h3>
                  <p>
                    Required for the platform to function — for example, keeping you logged in, maintaining session
                    security, and load-balancing. These cannot be switched off.
                  </p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Performance and Analytics Cookies</h3>
                  <p>
                    Help us understand how visitors interact with our platform (pages visited, time spent, errors
                    encountered) so we can measure and improve performance.
                  </p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Functional Cookies</h3>
                  <p>
                    Remember choices you make (such as language, region, or display preferences) to provide a more
                    personalized experience.
                  </p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Advertising and Targeting Cookies</h3>
                  <p>
                    Used to deliver advertising that is more relevant to you and your interests, and to measure the
                    effectiveness of marketing campaigns. These are only set with your consent.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">4. Third-Party Cookies</h2>
              <p>
                Some cookies are placed by third-party services that appear on our pages, such as analytics providers,
                payment processors, and advertising or marketing partners. These third parties may use cookies, web
                beacons, and similar technologies to collect information about your use of our platform and other
                websites, in accordance with their own privacy policies.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">5. Session and Persistent Cookies</h2>
              <p>
                <strong>Session cookies</strong> are temporary and are deleted from your device once you close your
                browser. <strong>Persistent cookies</strong> remain on your device for a set period, or until you
                delete them, and are used to remember your preferences or recognize you on return visits.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">6. Managing Your Cookie Preferences</h2>
              <p>
                You can manage or withdraw your consent to non-essential cookies at any time using the "Manage
                cookies" option in our cookie banner. You can also control or delete cookies through your browser
                settings — most browsers allow you to refuse cookies, delete existing cookies, or be alerted when a
                new cookie is set. Please note that blocking strictly necessary cookies may affect how our platform
                functions.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">7. Changes to This Cookie Policy</h2>
              <p>
                We may update this Cookie Policy from time to time to reflect changes in the cookies we use or for
                other operational, legal, or regulatory reasons. We encourage you to review this page periodically to
                stay informed about our use of cookies.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">8. Contact Us</h2>
              <p>If you have questions about this Cookie Policy, please contact us:</p>
              <div className="mt-2 space-y-1">
                <p><strong>Email:</strong> privacy@saasgama.com</p>
                <p><strong>Address:</strong> 5 Shenton Wy, #10-01, UIC Building, Singapore 068808</p>
                <p><strong>Phone:</strong> +65 8894 3169</p>
              </div>
            </section>

          </div>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
}
